import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { authService } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Mail, Lock, User, Eye, EyeOff, Check, X } from 'lucide-react';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { cn } from '@/lib/utils';
import { Turnstile } from '@/components/ui/Turnstile';

const Auth = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [signInTurnstileToken, setSignInTurnstileToken] = useState<string | null>(null);

  const returnTo = searchParams.get('returnTo') || '/';
  const mode = searchParams.get('mode');
  
  // Initialize view based on mode param (signup for partner flow)
  const [activeView, setActiveView] = useState<'signin' | 'signup'>(
    mode === 'signup' ? 'signup' : 'signin'
  );

  // Password validation
  const passwordValidation = {
    minLength: password.length >= 8,
    hasUpperCase: /[A-Z]/.test(password),
    hasLowerCase: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
    hasSymbol: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };

  const isPasswordValid = Object.values(passwordValidation).every(Boolean);

  // Redirect if already logged in
  useEffect(() => {
    if (user && !authLoading) {
      navigate(returnTo);
    }
  }, [user, authLoading, navigate, returnTo]);

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate password for signup
    if (activeView === 'signup' && !isPasswordValid) {
      toast({
        title: 'Invalid Password',
        description: 'Please meet all password requirements.',
        variant: 'destructive',
      });
      return;
    }

    // Check Turnstile for signup
    if (activeView === 'signup' && !turnstileToken) {
      toast({
        title: 'Verification Required',
        description: 'Please complete the security check.',
        variant: 'destructive',
      });
      return;
    }

    // Check Turnstile for signin
    if (activeView === 'signin' && !signInTurnstileToken) {
      toast({
        title: 'Verification Required',
        description: 'Please complete the security check.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      if (activeView === 'signin') {
        const { error } = await authService.signInWithPassword(email, password);
        if (error) throw error;
        
        toast({
          title: 'Welcome back!',
          description: 'You have successfully signed in.',
        });
        navigate(returnTo);
      } else {
        const { error } = await authService.signUp(email, password, fullName);
        if (error) throw error;
        
        // Set founder check flag for new signups
        localStorage.setItem('founder_check_pending', 'true');
        
        // Redirect to pricing page to show founder status
        navigate('/pricing?newUser=true');
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'An error occurred',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    try {
      const { error } = await authService.signInWithGoogle(returnTo);
      if (error) {
        toast({
          title: 'Error',
          description: error.message || 'Failed to sign in with Google',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'An error occurred',
        variant: 'destructive',
      });
    } finally {
      setGoogleLoading(false);
    }
  };

  const [resetTurnstileToken, setResetTurnstileToken] = useState<string | null>(null);

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast({
        title: 'Email Required',
        description: 'Please enter your email address.',
        variant: 'destructive',
      });
      return;
    }

    if (!resetTurnstileToken) {
      toast({
        title: 'Verification Required',
        description: 'Please complete the security check.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await authService.resetPassword(email);
      if (error) throw error;
      
      toast({
        title: 'Reset Email Sent',
        description: 'Check your email for password reset instructions.',
      });
      setShowForgotPassword(false);
      setResetTurnstileToken(null);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to send reset email',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center">
              {showForgotPassword 
                ? 'Reset Password' 
                : activeView === 'signin' 
                  ? 'Welcome Back' 
                  : 'Create Account'
              }
            </CardTitle>
            <CardDescription className="text-center">
              {showForgotPassword 
                ? 'Enter your email to receive reset instructions' 
                : activeView === 'signin' 
                  ? 'Sign in to your account to continue' 
                  : 'Create your account to get started'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {showForgotPassword ? (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reset-email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="reset-email"
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      required
                      disabled={loading}
                    />
                  </div>
                </div>
                <Turnstile
                  onVerify={setResetTurnstileToken}
                  onExpire={() => setResetTurnstileToken(null)}
                  onError={() => setResetTurnstileToken(null)}
                  className="mb-4"
                />
                <Button type="submit" className="w-full" disabled={loading || !resetTurnstileToken}>
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send Reset Link'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => setShowForgotPassword(false)}
                  disabled={loading}
                >
                  Back to Sign In
                </Button>
              </form>
            ) : (
              <div className="space-y-4">
                {/* Google Sign In Button */}
                <Button
                  type="button"
                  variant="outline"
                  className="w-full flex items-center justify-center gap-2"
                  onClick={handleGoogleSignIn}
                  disabled={loading || googleLoading}
                >
                  {googleLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <svg className="h-4 w-4" viewBox="0 0 24 24">
                      <path
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        fill="#4285F4"
                      />
                      <path
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        fill="#34A853"
                      />
                      <path
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        fill="#FBBC05"
                      />
                      <path
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        fill="#EA4335"
                      />
                    </svg>
                  )}
                  Continue with Google
                </Button>
                
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">Or continue with email</span>
                  </div>
                </div>

                {/* Sign In Form */}
                {activeView === 'signin' && (
                  <form onSubmit={handleEmailAuth} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="email"
                          type="email"
                          placeholder="your@email.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="pl-10"
                          required
                          disabled={loading}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="pl-10 pr-10"
                          required
                          disabled={loading}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <Turnstile
                      size="normal"
                      onVerify={setSignInTurnstileToken}
                      onExpire={() => setSignInTurnstileToken(null)}
                      onError={() => setSignInTurnstileToken(null)}
                      className="my-4"
                    />
                    <Button type="submit" className="w-full" disabled={loading || !signInTurnstileToken}>
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign In'}
                    </Button>
                    
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-sm text-muted-foreground hover:text-foreground hover:underline w-full text-center block"
                    >
                      Forgot your password?
                    </button>
                    
                    <div className="border-t border-border my-4" />
                    
                    <p className="text-sm text-center text-muted-foreground">
                      Don't have an account?{' '}
                      <button
                        type="button"
                        onClick={() => setActiveView('signup')}
                        className="text-circle-blue font-medium hover:underline"
                      >
                        Sign up
                      </button>
                    </p>
                  </form>
                )}

                {/* Sign Up Form */}
                {activeView === 'signup' && (
                  <form onSubmit={handleEmailAuth} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="signup-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="signup-email"
                          type="email"
                          placeholder="your@email.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="pl-10"
                          required
                          disabled={loading}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-name">Full Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="signup-name"
                          type="text"
                          placeholder="John Doe"
                          value={fullName}
                          onChange={(e) => setFullName(e.target.value)}
                          className="pl-10"
                          required
                          disabled={loading}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        We'll use this to personalize your experience
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="signup-password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="pl-10 pr-10"
                          required
                          disabled={loading}
                          minLength={8}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      
                      {/* Password Requirements */}
                      <div className="space-y-1 text-xs">
                        <p className="font-medium text-muted-foreground">Password must contain:</p>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            {passwordValidation.minLength ? (
                              <Check className="h-3 w-3 text-green-500" />
                            ) : (
                              <X className="h-3 w-3 text-muted-foreground" />
                            )}
                            <span className={cn(
                              passwordValidation.minLength ? "text-green-500" : "text-muted-foreground"
                            )}>
                              At least 8 characters
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {passwordValidation.hasUpperCase && passwordValidation.hasLowerCase ? (
                              <Check className="h-3 w-3 text-green-500" />
                            ) : (
                              <X className="h-3 w-3 text-muted-foreground" />
                            )}
                            <span className={cn(
                              passwordValidation.hasUpperCase && passwordValidation.hasLowerCase
                                ? "text-green-500"
                                : "text-muted-foreground"
                            )}>
                              Mix of uppercase and lowercase letters
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {passwordValidation.hasNumber ? (
                              <Check className="h-3 w-3 text-green-500" />
                            ) : (
                              <X className="h-3 w-3 text-muted-foreground" />
                            )}
                            <span className={cn(
                              passwordValidation.hasNumber ? "text-green-500" : "text-muted-foreground"
                            )}>
                              At least one number
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {passwordValidation.hasSymbol ? (
                              <Check className="h-3 w-3 text-green-500" />
                            ) : (
                              <X className="h-3 w-3 text-muted-foreground" />
                            )}
                            <span className={cn(
                              passwordValidation.hasSymbol ? "text-green-500" : "text-muted-foreground"
                            )}>
                              At least one symbol (!@#$%^&*)
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <Turnstile 
                      size="normal"
                      onVerify={setTurnstileToken}
                      onExpire={() => setTurnstileToken(null)}
                      onError={() => setTurnstileToken(null)}
                      className="mt-2"
                    />

                    <Button type="submit" className="w-full" disabled={loading || !isPasswordValid || !turnstileToken}>
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Account'}
                    </Button>
                    
                    <p className="text-xs text-center text-muted-foreground">
                      First 500 agents get Founder Access with full Pro benefits free
                    </p>
                    
                    <p className="text-sm text-center text-muted-foreground">
                      Already have an account?{' '}
                      <button
                        type="button"
                        onClick={() => setActiveView('signin')}
                        className="text-circle-blue font-medium hover:underline"
                      >
                        Sign in
                      </button>
                    </p>
                  </form>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
};

export default Auth;
